import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Chip,
  IconButton,
  Link,
  Sheet,
  Stack,
  Table,
  Typography,
} from "@mui/joy";
import Message from "@mui/icons-material/Message";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useTeam } from "../TeamLayout";
import { useTranslation } from "../../../i18n";
import HomeIcon from "@mui/icons-material/Home";
import {
  SupplierTableItemFragment,
  UpsertSupplierInput,
  useSupplierByIdQuery,
  useSupplierUpsertMutation,
} from "../../../__generated__/types-and-hooks";
import QTextInput from "../../../x-components/QTextInput";
import { useState } from "react";
import ContactsModal from "./ContactsModal";
import CountryFlag from "../../../components/CountryFlag";
import Email from "@mui/icons-material/EmailOutlined";
import WhatsApp from "@mui/icons-material/WhatsApp";
import Phone from "@mui/icons-material/LocalPhone";

const iconMap: Record<string, React.ReactNode> = {
  phone: <Phone style={{ fontSize: 14 }} />,
  email: <Email style={{ fontSize: 14 }} />,
  whatsapp: <WhatsApp style={{ fontSize: 14 }} />,
};

export default function Supplier() {
  const t = useTranslation();
  const teamCtx = useTeam();
  const { uniqueId } = useParams();
  const supplierQuery = useSupplierByIdQuery({
    variables: { uniqueId: uniqueId! },
  });
  const [contactsModalOpen, setContactsModalOpen] =
    useState<SupplierTableItemFragment | null>(null);

  const supplier = supplierQuery?.data?.supplier_by_id;
  const [supplierUpsertMutation] = useSupplierUpsertMutation({});
  const updateFn = async (
    updates: Omit<UpsertSupplierInput, "uniqueId" | "teamId">
  ) => {
    const res = await supplierUpsertMutation({
      variables: {
        supplier: {
          teamId: teamCtx.team.id,
          uniqueId: supplier?.uniqueId!,
          ...updates,
        },
      },
      onCompleted(data) {
        supplierQuery.refetch();
      },
    });
    return res.errors?.map((err) => err.message).join("; ");
  };

  function onEditContactsClick(row: SupplierTableItemFragment) {
    setContactsModalOpen(row);
  }

  return (
    <>
      <Breadcrumbs
        aria-label="breadcrumbs"
        sx={{
          "--Breadcrumbs-gap": "4px",
          m: 1,
        }}
        size="sm"
      >
        <Link component={RouterLink} color="neutral" to="/">
          <HomeIcon />
        </Link>
        <Link
          component={RouterLink}
          color="neutral"
          to={`/${teamCtx.team.slug}`}
        >
          {teamCtx.team.name}
        </Link>
        <Link
          component={RouterLink}
          color="neutral"
          to={`/${teamCtx.team.slug}/suppliers`}
        >
          {t("Suppliers")}
        </Link>
        <Typography level="title-sm">#{uniqueId?.toUpperCase()}</Typography>
      </Breadcrumbs>

      <Box
        sx={{
          p: 2,
          columns: "400px",
        }}
      >
        {supplier && (
          <Card
            sx={{
              backgroundColor: "hsl(0deg 0% 31.06% / 35%);",
              padding: 0,
              mb: 2,
              breakInside: "avoid",
            }}
            variant="outlined"
          >
            <Typography sx={{ pt: 1, pl: 2 }} level="title-lg">
              {t("Supplier")}
            </Typography>

            <CardContent
              sx={{
                m: 0,
                margin: "2px",
                borderRadius: 5,
                border: "1px solid black",
                bgcolor: "background.surface",
                gap: 0,
                p: 1,
                pl: 2,
              }}
            >
              <Sheet sx={{ overflow: "auto" }}>
                <Table
                  borderAxis="xBetween"
                  sx={{
                    p: 0,
                    m: 0,
                    "& tbody td:nth-child(1)": { width: 180 },
                    "& tbody td": { pl: 0 },
                  }}
                >
                  <tbody>
                    <tr>
                      <td>
                        <Typography level="body-sm">{t("Name")}</Typography>
                      </td>
                      <td>
                        <QTextInput
                          val={supplier.name}
                          update={(name) => updateFn({ name: name })}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography level="body-sm">{t("Country")}</Typography>
                      </td>
                      <td>
                        <QTextInput
                          val={supplier.countryCode ?? ""}
                          update={(countryCode) =>
                            updateFn({ countryCode: countryCode })
                          }
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography level="body-sm">
                          {t("Address Line 1")}
                        </Typography>
                      </td>
                      <td>
                        <QTextInput
                          val={supplier.addressLine1 ?? ""}
                          update={(addressLine1) => updateFn({ addressLine1 })}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography level="body-sm">
                          {t("Address Line 2")}
                        </Typography>
                      </td>
                      <td>
                        <QTextInput
                          val={supplier.addressLine2 ?? ""}
                          update={(addressLine2) => updateFn({ addressLine2 })}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography level="body-sm">{t("City")}</Typography>
                      </td>
                      <td>
                        <QTextInput
                          val={supplier.city ?? ""}
                          update={(city) => updateFn({ city })}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography level="body-sm">{t("Zip Code")}</Typography>
                      </td>
                      <td>
                        <QTextInput
                          val={supplier.postalCode ?? ""}
                          update={(postalCode) => updateFn({ postalCode })}
                        />
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Sheet>
            </CardContent>
          </Card>
        )}
        {supplier && (
          <Card
            sx={{
              backgroundColor: "hsl(0deg 0% 31.06% / 35%);",
              padding: 0,
              mb: 2,
              breakInside: "avoid",
            }}
            variant="outlined"
          >
            <Typography sx={{ pt: 1, pl: 2 }} level="title-lg">
              {t("Contacts")}
              <IconButton
                sx={{ fontSize: 12 }}
                size="sm"
                color="primary"
                variant="plain"
                onClick={() => onEditContactsClick(supplier)}
              >
                <ModeEditIcon style={{ fontSize: 14 }} />
              </IconButton>
            </Typography>

            <CardContent
              sx={{
                m: "2px",
                borderRadius: 5,
                border: "1px solid black",
                bgcolor: "background.surface",
                gap: 0,
                pt: "4px",
              }}
            >
              <Stack gap={0.5} sx={{ mb: 0.5 }}>
                {supplier?.contacts?.map((contact) => (
                  <Card
                    variant="outlined"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      padding: 1.5,
                      ml: 0.5,
                      mr: 0.5,
                      backgroundColor: "hsl(0deg 0% 31.06% / 28%);",
                    }}
                  >
                    <Typography level="title-sm">{contact?.name}</Typography>
                    {contact.contactMethods.map((method, index) => (
                      <Chip
                        key={index}
                        variant="soft"
                        sx={{ borderRadius: 4 }}
                        startDecorator={
                          iconMap[method.key] ?? (
                            <Message style={{ fontSize: 14 }} />
                          )
                        }
                      >
                        {method.value}
                      </Chip>
                    ))}
                    <KeyValueLine
                      label={t("Address Line 1")}
                      value={contact.addressLine1}
                    />
                    <KeyValueLine
                      label={t("Address Line 2")}
                      value={contact.addressLine2}
                    />
                    <KeyValueLine label={t("City")} value={contact.city} />
                    <KeyValueLine label={t("State")} value={contact.state} />
                    <KeyValueLine
                      label={t("Postal Code")}
                      value={contact.postalCode}
                    />
                    <KeyValueLine
                      label={t("Country")}
                      value={
                        contact.countryCode ? (
                          <CountryFlag code={contact.countryCode} />
                        ) : null
                      }
                    />
                  </Card>
                ))}
              </Stack>
            </CardContent>
          </Card>
        )}
      </Box>
      {contactsModalOpen && (
        <ContactsModal
          supplier={contactsModalOpen}
          onSave={() => {
            supplierQuery.refetch();
          }}
          onClose={() => {
            setContactsModalOpen(null);
          }}
        />
      )}
    </>
  );
}

function KeyValueLine({
  label,
  value,
}: {
  label: string;
  value?: string | null | React.ReactNode;
}) {
  if (!value) return null;
  return (
    <Stack direction="row" gap={1}>
      <Typography sx={{ minWidth: 140 }} level="body-md">
        {label}:
      </Typography>
      <Typography>{value}</Typography>
    </Stack>
  );
}
